/** @jsx isolateComponent */
import { css } from '@emotion/core';
import { isolateComponent } from '@zapier/style-encapsulation';
import { Colors, Link, Typography } from '@zapier/design-system';

type Props = {
  /**
   * The base URL after which all of the letters render.
   */
  baseUrl: string;
  /**
   * The text to render above the alphabetic navigation
   */
  title: string;
};

const Styles = {
  root: css`
    display: grid;
    grid-template-columns: 100%;
    gap: 5px;
    justify-items: center;
    ${Typography.smallPrint3};
    text-align: center;
  `,
  title: css`
    font-weight: 700;
    color: ${Colors.gray60};
  `,
  links: css`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
  `,
  link: css`
    // Increase hit size.
    display: block;
    padding: 3px;
    margin: 0 1px;
    white-space: nowrap;
    flex: none;
    color: ${Colors.gray60};
    cursor: pointer;
    text-decoration: none;
    &:hover,
    &:focus {
      color: ${Colors.ocean};
      outline: none;
    }
  `,
};

// "Letters"
const letters = ['0–9', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')];

/**
 * @private
 * Renders an alphabetic navigation for browsing apps.
 */
export const AlphabeticAppNav = (props: Props) => {
  const linkItems = letters.map((letter) => {
    const href = `${props.baseUrl}/${letter.toLowerCase()}`
      .replace('//', '/')
      .replace('–', '-');
    return (
      <li key={letter}>
        <Link
          ariaLabel={`${letter} — Apps beginning with ${letter}`}
          color={null}
          css={Styles.link}
          href={href}
        >
          {letter}
        </Link>
      </li>
    );
  });
  return (
    <section css={Styles.root}>
      <h2 css={Styles.title}>{props.title}</h2>
      <ul css={Styles.links}>{linkItems}</ul>
    </section>
  );
};

AlphabeticAppNav.defaultProps = {
  baseUrl: '/find-apps/',
  title: 'Apps by Title',
};
